import React, { useState } from 'react';
import { FileEarmarkText, FileEarmarkImage, FileEarmarkPdf } from 'react-bootstrap-icons';
import { Modal } from 'react-bootstrap';
import { FileAttachment } from '../types/chat';
import './AttachmentDisplay.css';

interface AttachmentDisplayProps {
  attachments: FileAttachment[];
}

const AttachmentDisplay: React.FC<AttachmentDisplayProps> = ({ attachments }) => {
  const [selectedImage, setSelectedImage] = useState<FileAttachment | null>(null);

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) {
      return <FileEarmarkPdf size={20} />;
    } else if (type.includes('image')) {
      return <FileEarmarkImage size={20} />;
    } else {
      return <FileEarmarkText size={20} />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i)) + sizes[i];
  };

  const truncateFileName = (name: string, maxLength: number = 25): string => {
    if (name.length <= maxLength) return name;

    const extension = name.substring(name.lastIndexOf('.'));
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  };

  const isImage = (type: string) => type.toLowerCase().includes('image');

  const getImageSrc = (attachment: FileAttachment): string => {
    if (attachment.url) {
      return attachment.url;
    } else if (attachment.data) {
      return `data:${attachment.type};base64,${attachment.data}`;
    }
    return '';
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div className="attachment-display">
        {attachments.map((attachment) => {
          const isImageAttachment = isImage(attachment.type);
          const imageSrc = isImageAttachment ? getImageSrc(attachment) : '';

          return (
            <div key={attachment.id}>
              {isImageAttachment && imageSrc ? (
                <div
                  className="attachment-image-container"
                  onClick={() => setSelectedImage(attachment)}
                >
                  <img
                    src={imageSrc}
                    alt={attachment.name}
                    className="attachment-image"
                  />
                  <div className="attachment-image-caption">
                    {attachment.name}
                  </div>
                </div>
              ) : (
                <div className="attachment-item">
                  <div className="attachment-icon">
                    {getFileIcon(attachment.type)}
                  </div>
                  <div className="attachment-info">
                    <div className="attachment-name" title={attachment.name}>
                      {truncateFileName(attachment.name)}
                    </div>
                    <div className="attachment-size">
                      {formatFileSize(attachment.size)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedImage && (
        <Modal
          show={!!selectedImage}
          onHide={() => setSelectedImage(null)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{selectedImage.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={getImageSrc(selectedImage)}
              alt={selectedImage.name}
              className="attachment-modal-image"
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default AttachmentDisplay;
